import { serviceHelper } from '../../../../config/service-helper';
import { getStoreCountryName } from '../../../../../scripts/utils/multiTenancyHeaders';
import {
  SyndicatedContentProvider,
  SyndicatedContentProductData,
  SyndicatedContentConfig,
  SyndicatedContentResponse,
  SyndicatedContentKeys,
} from './types';
import { SYNDICATED_CONTENT_PROVIDER_NAME, FLIX_MATCH_EVENT, SYNDICATED_CONTENT_KEYS } from './constants';

/**
 * Wraps a promise with a timeout that resolves to a fallback value if the promise doesn't settle in time
 * @param promise - The promise to wrap
 * @param timeoutMs - Timeout duration in milliseconds
 * @param fallbackValue - Value to resolve with if timeout occurs
 * @returns Promise that resolves to either the original promise result or the fallback value
 */
const withTimeout = <T>(promise: Promise<T>, timeoutMs: number, fallbackValue: T): Promise<T> => {
  return Promise.race([promise, new Promise<T>(resolve => setTimeout(() => resolve(fallbackValue), timeoutMs))]);
};

class SyndicatedContentData {
  product: SyndicatedContentProductData;
  config: SyndicatedContentConfig;

  constructor(product: SyndicatedContentProductData) {
    this.product = product;

    this.config = {
      region: getStoreCountryName(),
      iso: window.Jbhifi.storeLocation,
      lang: window.Jbhifi.storeLocation === 'NZ' ? 'nz' : 'au',
    };
  }

  /*
   * Fetch Syndicated Content Providers
   */
  async getProviders(): Promise<Array<SyndicatedContentProvider | null>> {
    const syndicatedContentChecker = new SyndicatedContentData.fetch(this.product, this.config);
    const providers = await syndicatedContentChecker.contentProviders();

    return providers.filter(provider => provider) || null;
  }

  /*
   * Load Syndicated Content
   */
  loadContent(providers: Array<SyndicatedContentProvider | null>): void {
    if (!providers || !providers.length || !this.config) return;

    const loader = new SyndicatedContentData.loaders(this.product, this.config);

    /*
     * Load content for each provider
     * Note: Ignore Syndigo as it's loaded when checking for providers
     */
    providers.forEach(provider => {
      switch (provider?.type) {
        case SYNDICATED_CONTENT_PROVIDER_NAME.CONTENT_DRIVER:
          loader.contentDriver();
          break;
        case SYNDICATED_CONTENT_PROVIDER_NAME.FLIX:
          loader.flix();
          break;
        case SYNDICATED_CONTENT_PROVIDER_NAME.ONE_WORLD_SYNC:
          loader.oneWorldSync();
          break;
      }
    });

    /*
     * Always load Flix
     * Load Flix even if it's not in the providers array to alert
     * the provider that the product doesn't have content.
     */
    if (!providers.find(provider => provider?.type === SYNDICATED_CONTENT_PROVIDER_NAME.FLIX)) {
      loader.flix();
    }
  }

  /*
   * Fetch Syndicated Content Providers
   */
  static fetch = class {
    product: SyndicatedContentProductData;
    config: SyndicatedContentConfig;
    keys: SyndicatedContentKeys;
    private _flixContent: Array<SyndicatedContentProvider> | null = null;

    constructor(product: SyndicatedContentProductData, config: SyndicatedContentConfig) {
      this.product = product;
      this.config = config;
      this.keys = SYNDICATED_CONTENT_KEYS[this.config.iso];
    }

    async contentProviders(): Promise<Array<SyndicatedContentProvider | null>> {
      const primaryContent = await this.checkPrimaryContent();

      // Query flix for complimentary content if primary content is found
      if (primaryContent) {
        const flixContent = await this.flix();
        const flixComplimentaryContent = flixContent ? flixContent.find(content => content.complimentary) : null;
        return flixComplimentaryContent ? [primaryContent, flixComplimentaryContent] : [primaryContent];
      }

      // Otherwise, we'll check alternate providers
      const alternateContent = await this.checkAlternateProviders();
      return alternateContent ? [alternateContent] : [];
    }

    private async checkPrimaryContent(): Promise<SyndicatedContentProvider | null> {
      try {
        const primaryContent = await this.contentDriver();
        return primaryContent;
      } catch (error) {
        console.error('There was an error while fetching primary content:', error);
        return null;
      }
    }

    private async checkAlternateProviders(): Promise<SyndicatedContentProvider | null> {
      try {
        const flix = await this.flix();
        if (flix && flix.length > 0 && !flix[0].complimentary) {
          return flix[0];
        }

        const syndigo = await this.syndigo();
        if (syndigo) {
          return syndigo;
        }

        const oneWorldSync = await this.oneWorldSync();
        if (oneWorldSync) {
          return oneWorldSync;
        }

        return null;
      } catch (error) {
        console.error('There was an error while fetching alternate providers:', error);
        return null;
      }
    }

    // Get Content Driver content
    private async contentDriver(): Promise<SyndicatedContentProvider | null> {
      const { Content, Product } = await import('https://global.contentdriver.com/jbhifi.com.au/library.js');

      try {
        const content = await withTimeout(Content.forProduct(await Product.current()), 5000, null);
        if (content) {
          return {
            type: SYNDICATED_CONTENT_PROVIDER_NAME.CONTENT_DRIVER,
            complimentary: false,
          };
        }
        return null;
      } catch (error) {
        throw new Error(`Error calling Content Driver: ${error}`);
      }
    }

    // Get Flix content
    private async flix(): Promise<Array<SyndicatedContentProvider> | null> {
      const { productModel, productEAN13 } = this.product;
      const { FLIX_DISTRIBUTOR } = this.keys;
      const storeLocation = window.Jbhifi?.storeLocation?.toLocaleLowerCase() ?? 'au';

      const url = `https://media.flixcar.com/delivery/webcall/match/${FLIX_DISTRIBUTOR}/${storeLocation}/mpns/${encodeURIComponent(
        productModel
      )}/ean/${encodeURIComponent(productEAN13 ?? '')}?resp=get_info`;

      if (this._flixContent) return this._flixContent;

      try {
        const response = await fetch(url);

        const data: Array<SyndicatedContentResponse['flix']> | SyndicatedContentResponse['flix'] =
          await response.json();
        if (!data) return null;

        /*
         * Flix responses may differ depending on the product.
         */
        const matches = Array.isArray(data) ? data : [data];
        const filteredMatches = matches
          .filter(item => item.event === FLIX_MATCH_EVENT.VALID)
          .map(match => {
            return {
              type: SYNDICATED_CONTENT_PROVIDER_NAME.FLIX,
              complimentary: !!match.complimentary,
            };
          });
        this._flixContent = filteredMatches;
        return filteredMatches;
      } catch (error) {
        return null;
      }
    }

    // Get Syndigo content
    private async syndigo(): Promise<SyndicatedContentProvider | null> {
      const { productSku } = this.product;
      const { syndigoId } = serviceHelper.syndicatedContent;

      if (!syndigoId) {
        return Promise.reject('Syndigo not enabled for this environment');
      }

      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.id = 'syndigo-script';
      script.src = `https://content.syndigo.com/site/${syndigoId}/tag.js?cv=${Math.floor(Date.now() / 86400000)}`;

      const firstScriptTag = document.getElementsByTagName('script')[0];
      if (firstScriptTag.parentNode) {
        firstScriptTag.parentNode.insertBefore(script, firstScriptTag);
      }

      // Wait for SYNDI initialization
      await withTimeout(
        new Promise(resolve => {
          const interval = setInterval(() => {
            if (window.SYNDI) {
              clearInterval(interval);
              resolve(true);
            }
          }, 200);
        }),
        5000,
        false
      );

      window.SYNDI.push(productSku);

      const hasContent = await withTimeout(
        new Promise<boolean>(resolve => {
          window.SYNDI.push({
            contentCallback: (hasContent: boolean) => {
              resolve(hasContent);
            },
          });
        }),
        5000,
        false
      );

      return hasContent
        ? Promise.resolve({ type: SYNDICATED_CONTENT_PROVIDER_NAME.SYNDIGO, complimentary: false })
        : Promise.resolve(null);
    }

    // Get OneWorldSync content
    private async oneWorldSync(): Promise<SyndicatedContentProvider | null> {
      const { productBrand, productModel } = this.product;
      const { ONEWORLD_SKEY, ONEWORLD_ZONE_ID } = this.keys;
      const url = `https://ws.cs.1worldsync.com/${ONEWORLD_SKEY}/contentcheck/${ONEWORLD_ZONE_ID}?mf=${productBrand}&pn=${productModel}&lang=en-${this.config.lang.toLocaleUpperCase()}&mode=json`;

      try {
        const response = await withTimeout(fetch(url), 5000, null as any);
        if (!response.ok) return null;

        const result: SyndicatedContentResponse['oneWorldSync'] = await response.json();
        if (!result?.data || !result.data?.['ccs-inline-content']?.available) {
          return null;
        }

        return {
          type: SYNDICATED_CONTENT_PROVIDER_NAME.ONE_WORLD_SYNC,
          complimentary: false,
        };
      } catch (error) {
        return null;
      }
    }
  };

  /*
   * Load Syndicated Content
   */
  static loaders = class {
    product: SyndicatedContentProductData;
    config: SyndicatedContentConfig;
    keys: SyndicatedContentKeys;

    constructor(product: SyndicatedContentProductData, config: SyndicatedContentConfig) {
      this.product = product;
      this.config = config;
      this.keys = SYNDICATED_CONTENT_KEYS[this.config.iso];
    }

    // Load Content Driver content
    async contentDriver(): Promise<void> {
      const { Content, Product } = await import('https://global.contentdriver.com/jbhifi.com.au/library.js');
      const contentDriverContent = await Content.forProduct(await Product.current());
      contentDriverContent.embed();
    }

    // Load Flix content
    flix(): void {
      const { productModel, productEAN13 } = this.product;
      const { FLIX_DISTRIBUTOR } = this.keys;
      const script = document.createElement('script');
      script.async = true;
      script.setAttribute('data-flix-distributor', FLIX_DISTRIBUTOR);
      script.setAttribute('data-flix-language', this.config.lang);
      script.setAttribute('data-flix-ean', productEAN13 || '');
      script.setAttribute('data-flix-mpn', productModel || '');
      script.setAttribute('data-flix-inpage', 'flix-wrapper');
      script.setAttribute('data-flix-hotspot', 'true');
      // script.setAttribute('data-flix-autoload', 'inpage');
      document.body.appendChild(script);
      script.src = 'https://media.flixfacts.com/js/loader.js';
    }

    // Load OneWorldSync content
    oneWorldSync(): void {
      const { productSku, productBrand, productModel } = this.product;
      const { ONEWORLD_SKEY, ONEWORLD_ZONE_ID } = this.keys;

      window.ccs_cc_args = [];
      if (productSku) window.ccs_cc_args.push(['cpn', productSku]);
      if (productBrand) window.ccs_cc_args.push(['mf', productBrand]);
      if (productModel) window.ccs_cc_args.push(['pn', productModel]);

      window.ccs_cc_args.push(['lang', 'EN']);
      window.ccs_cc_args.push(['market', this.config.iso]);
      window.ccs_cc_args.push(['_SKey', ONEWORLD_SKEY]);
      window.ccs_cc_args.push(['_ZoneId', ONEWORLD_ZONE_ID]);

      const script = document.createElement('script');
      script.src = 'https://cdn.cs.1worldsync.com/jsc/h1ws.js';

      const firstScriptTag = document.getElementsByTagName('script')[0];
      if (firstScriptTag.parentNode) {
        firstScriptTag.parentNode.insertBefore(script, firstScriptTag);
      }
    }
  };
}

export { SyndicatedContentData };
